import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CalendarX, Bell, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BookingActivationCardProps {
  listingId: string;
  listingName: string;
}

export function BookingActivationCard({ listingId, listingName }: BookingActivationCardProps) {
  const storageKey = `booking-activation-${listingId}`;
  const [submitted, setSubmitted] = useState(() => sessionStorage.getItem(storageKey) === "true");
  const [loading, setLoading] = useState(false);
  const [visitorEmail, setVisitorEmail] = useState("");

  const handleRequest = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("request-booking-activation", {
        body: { listing_id: listingId, visitor_email: visitorEmail || undefined },
      });

      if (error) throw error;

      sessionStorage.setItem(storageKey, "true");
      setSubmitted(true);
      toast.success("¡Solicitud enviada! Le notificaremos al negocio.");
    } catch (err) {
      console.error("Booking activation request error:", err);
      toast.error("Error al enviar la solicitud. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const displayName = listingName || "Este negocio";

  return (
    <Card className="border-2 border-orange-400/50 bg-gradient-to-br from-orange-50/80 via-amber-50/50 to-background dark:from-orange-950/30 dark:via-amber-950/20 dark:to-background overflow-hidden relative">
      <div className="absolute top-0 right-0 w-28 h-28 bg-orange-400/10 rounded-full -mr-14 -mt-14 blur-xl" />

      <CardHeader className="relative pb-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 rounded-md bg-orange-100 dark:bg-orange-900/40">
            <CalendarX className="w-4 h-4 text-orange-600 dark:text-orange-400" />
          </div>
          <span className="text-xs font-semibold text-orange-700 dark:text-orange-300 uppercase tracking-wide">
            Calendario de Reservas
          </span>
        </div>
        <CardTitle className="text-lg leading-snug">
          Reservas online aún no disponibles
        </CardTitle>
      </CardHeader>

      <CardContent className="relative space-y-3">
        {submitted ? (
          <div className="flex flex-col items-center gap-2 py-2 text-center">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
            <p className="text-sm font-medium text-green-700 dark:text-green-400">
              ¡Solicitud enviada!
            </p>
            <p className="text-xs text-muted-foreground">
              Hemos notificado a {displayName} de tu interés.
            </p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <strong>{displayName}</strong> aún no ha activado su calendario de reservas online. ¡Hazle saber que estás interesado!
            </p>

            <div className="space-y-2">
              <Input
                type="email"
                placeholder="Tu email (opcional)"
                value={visitorEmail}
                onChange={(e) => setVisitorEmail(e.target.value)}
                className="text-sm h-9"
              />
            </div>

            <Button
              className="w-full bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleRequest}
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Enviando...
                </span>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Solicitar Activación de Reservas
                </>
              )}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
